import { useState } from 'react';

import { Box, ButtonBase, Chip, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { type ClaimPart } from '@/karehero-app/tell-us-once/intake/content';

/**
 * A claim in its parts, inside a card from us, with a way between them.
 *
 * Two views of the same thing. All of it: each part with a line on what we
 * will ask, a preview before anything has started. One part: what it still
 * needs, ticked as it comes in, once they have started on it. The chips along
 * the top switch between them, All first, so moving back and forth is one tap
 * either way and the part they are on is always marked.
 *
 * A part only shows as started once something said has touched it: the dot
 * on its chip, and how far it is in the list.
 */
const ClaimParts = ({
  parts,
  initialView = 'all',
}: {
  parts: ClaimPart[];
  /** Which view opens: 'all', or a part by its index. */
  initialView?: 'all' | number;
}) => {
  const [view, setView] = useState(initialView);
  const done = (p: ClaimPart) => p.found?.filter(Boolean).length ?? 0;
  const started = (p: ClaimPart) => done(p) > 0;

  return (
    <Stack spacing={2}>
      {/* The way between the views. Scrolls sideways rather than wrapping:
          wrapped, the chips took more room than the part they were for. */}
      <Stack
        sx={{ overflowX: 'auto', mx: -2, px: 2, pb: 0.5 }}
        direction='row'
        spacing={0.75}
      >
        <Chip
          size='small'
          label='All'
          color={view === 'all' ? 'primary' : 'default'}
          variant={view === 'all' ? 'filled' : 'outlined'}
          onClick={() => setView('all')}
        />
        {parts.map((p, i) => (
          <Chip
            key={p.title}
            size='small'
            label={p.short}
            color={view === i ? 'primary' : 'default'}
            variant={view === i ? 'filled' : 'outlined'}
            onClick={() => setView(i)}
            // Started parts carry a dot, so the row says where they have got
            // to without opening anything.
            icon={
              started(p) ? (
                <Box sx={{ display: 'flex', color: 'custom.coral.600' }}>
                  <Icon icon={'circle' as never} type='fas' fontSize={6} />
                </Box>
              ) : undefined
            }
          />
        ))}
      </Stack>

      {view === 'all' ? (
        <Stack spacing={1.5}>
          {parts.map((p, i) => (
            <ButtonBase
              key={p.title}
              onClick={() => setView(i)}
              sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
            >
              <Stack
                sx={{ alignItems: 'center', width: '100%' }}
                direction='row'
                spacing={1}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ color: 'text.primary' }} variant='body2'>
                    {p.title}
                  </Typography>
                  <Typography
                    sx={{ color: 'custom.neutral.600' }}
                    variant='caption'
                  >
                    {p.line}
                  </Typography>
                </Box>
                {started(p) ? (
                  <Typography
                    sx={{ color: 'custom.coral.600', flexShrink: 0 }}
                    variant='caption'
                  >
                    {done(p)} of {p.needs.length}
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', color: 'text.secondary' }}>
                    <Icon icon='chevron-right' fontSize={12} />
                  </Box>
                )}
              </Stack>
            </ButtonBase>
          ))}
        </Stack>
      ) : (
        <Stack spacing={1}>
          <Typography sx={{ color: 'text.primary' }} variant='subtitle2'>
            {parts[view].title}
          </Typography>
          {parts[view].needs.map((need, j) => {
            const got = parts[view].found?.[j];
            return (
              <Stack
                key={need}
                sx={{ alignItems: 'flex-start' }}
                direction='row'
                spacing={1}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexShrink: 0,
                    pt: 0.25,
                    color: got ? 'success.main' : 'text.disabled',
                  }}
                >
                  <Icon
                    icon={(got ? 'circle-check' : 'circle') as never}
                    fontSize={14}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: got ? 'text.primary' : 'custom.neutral.600',
                    }}
                    variant='body2'
                  >
                    {need}
                  </Typography>
                  {got && (
                    <Typography
                      sx={{ color: 'custom.neutral.600' }}
                      variant='caption'
                    >
                      {got}
                    </Typography>
                  )}
                </Box>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default ClaimParts;
